import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Location } from '@cobliteam/common-types';
import { useRiskRecognitionHighlight } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useRightPanelStore from '@cobliteam/hooks/src/useRightPanelStore/useRightPanelStore';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import { cn } from '@cobliteam/ui/utils';
import { type TransitionEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useRiskRecognition, { buildRiskRecognitionEventProps, isHighlightRenderable } from '../../store/useRiskRecognition';
import AIIcon from '../AIIcon/AIIcon';
import RiskHighlightText, { renderRiskHighlight } from './RiskHighlightText';

const trackDisplayedOncePerBucket = (highlight: RiskRecognitionHighlight) => {
  const { dismissedBucketEnd, lastDisplayedBucketEnd, markDisplayed } = useRiskRecognition.getState();
  if (!isHighlightRenderable(highlight, dismissedBucketEnd) || highlight.time_bucket_end === lastDisplayedBucketEnd) return;
  Analytics.track('AI - Risk Recognition - Displayed', buildRiskRecognitionEventProps(highlight));
  markDisplayed(highlight.time_bucket_end);
};

const RiskRecognitionFloatingCard = () => {
  const { t } = useTranslation('ai');
  const { aiRiskRecognitionHighlights } = useFeatureFlags();
  const { pathname } = useLocation();

  const dismissedBucketEnd = useRiskRecognition((s) => s.dismissedBucketEnd);
  const routeFollowBucketEnd = useRiskRecognition((s) => s.routeFollowBucketEnd);
  const dismiss = useRiskRecognition((s) => s.dismiss);
  const openDrawer = useRiskRecognition((s) => s.openDrawer);
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  // The card hugs the 440px right panel (on /monitoring and /tracking) with a 1rem gap. When the page
  // collapses that panel, the card slides right to keep the same 1rem gap from the viewport edge.
  const isRightPanelHidden = useRightPanelStore((s) => s.isRightPanelHidden);

  // Polling stays monitoring-only: landing on /tracking disables the query, freezing the
  // highlight so a new bucket can't swap the card to another vehicle mid-route-view. Rendering
  // also follows the acted-on alert onto /tracking, pinned to its bucket — a newer bucket or a
  // direct visit (without "Ver trajeto") shows nothing.
  const pollingEnabled = aiRiskRecognitionHighlights && pathname === Location.MONITORING;

  // The highlight is server state: it lives in the query cache, not the store. While the query is
  // disabled (off /monitoring) it keeps returning the last cached bucket — that frozen value is
  // what keeps the card pinned to the followed alert on /tracking.
  const { data: highlight } = useRiskRecognitionHighlight(pollingEnabled);

  const isFollowingOnTracking = pathname === Location.TRACKING && !!routeFollowBucketEnd && highlight?.time_bucket_end === routeFollowBucketEnd;
  const renderAllowed = aiRiskRecognitionHighlights && (pathname === Location.MONITORING || isFollowingOnTracking);

  // Keyed by bucket (not a boolean) so the next bucket's fresh highlight never
  // inherits a stale "exiting" flag and auto-dismisses.
  const [exitingBucketEnd, setExitingBucketEnd] = useState<string | null>(null);

  // Fire "Displayed" once per bucket as a renderable highlight arrives. Structural sharing keeps the
  // same bucket's object reference stable, so this runs only on a genuinely new bucket; the helper
  // also guards dismissed / non-renderable ones.
  useEffect(() => {
    if (highlight) trackDisplayedOncePerBucket(highlight);
  }, [highlight]);

  if (!renderAllowed) return null;
  if (!isHighlightRenderable(highlight, dismissedBucketEnd)) return null;
  if (!highlight?.metadata) return null;

  const isExiting = exitingBucketEnd === highlight.time_bucket_end;

  const handleDismiss = () => {
    Analytics.track('AI - Risk Recognition - Dismissed', buildRiskRecognitionEventProps(highlight));
    // Fire the personal silence (~2h) in parallel with the exit animation. Guarded:
    // without a detection_id (backend not yet shipping it) we still hide locally.
    const detectionId = highlight.metadata?.detection_id;
    if (detectionId) createSilence({ detectionId, reason: 'dismissed' });
    setExitingBucketEnd(highlight.time_bucket_end);
  };

  const handleExitEnd = (event: TransitionEvent<HTMLDivElement>) => {
    // The opacity leg settles the dismissal; the transform leg fires its own transitionend we ignore.
    // Dismiss the captured exiting bucket, not the live highlight — a new bucket may have arrived mid-animation.
    if (event.propertyName !== 'opacity' || !exitingBucketEnd) return;
    dismiss(exitingBucketEnd);
    setExitingBucketEnd(null);
  };

  const handleAction = () => {
    Analytics.track('AI - Risk Recognition - Action Clicked', buildRiskRecognitionEventProps(highlight));
    openDrawer();
  };

  return (
    <div
      className={cn(
        'fixed bottom-4 z-[1003] w-[272px] transition-[opacity,transform,right] duration-200 ease-in motion-reduce:duration-[1ms]',
        isRightPanelHidden ? 'right-4' : 'right-[calc(440px+1rem)]',
        isExiting && 'pointer-events-none translate-y-2 scale-[0.98] opacity-0'
      )}
      onTransitionEnd={handleExitEnd}
    >
      <div className='relative overflow-hidden rounded-[18px] p-0.5 shadow-[0px_20px_25px_-5px_rgba(0,27,184,0.1),0px_8px_10px_-6px_rgba(0,27,184,0.1)]'>
        {/* Animated gradient border: a centered conic-gradient square spun with the built-in `spin`
            keyframes — rotating a centered conic gradient equals animating its `from` angle.
            Sized past the card diagonal so the ring stays filled at every rotation. */}
        <span
          aria-hidden
          className='pointer-events-none absolute top-1/2 left-1/2 aspect-square w-[200%] -translate-x-1/2 -translate-y-1/2 [animation:spin_2.5s_linear_infinite] [background:conic-gradient(#b91c1c_0deg,#ef4444_55deg,#fda4af_100deg,#fff1f2_135deg,#fff1f2_170deg,#fda4af_235deg,#ef4444_320deg,#b91c1c_360deg)] motion-reduce:[animation:none] motion-reduce:[background:#dc2626]'
        />
        <div className='relative flex w-full flex-col gap-4 rounded-[16px] bg-white pt-4 pb-[18px]'>
          <div className='flex flex-col gap-1.5 px-5'>
            <Typography variant='text-lg' className='text-[#dc2626]'>
              {renderRiskHighlight(highlight.title, 'font-bold')}
            </Typography>
            <RiskHighlightText text={highlight.description} className='text-[#020231]' />
          </div>

          <div className='flex flex-row items-center gap-2.5 px-5'>
            <button
              type='button'
              onClick={handleDismiss}
              className='inline-flex h-9 items-center justify-center rounded-lg border border-[#e4e4e7] border-solid bg-white px-3 shadow-[0px_1px_2px_rgba(0,27,184,0.05)] transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#020231]'>
                {t('risk_recognition.dismiss')}
              </Typography>
            </button>
            <button
              type='button'
              onClick={handleAction}
              className='inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#0745cf] to-[#9847ee] px-3 text-white shadow-[1px_5px_11px_0px_rgba(152,71,238,0.32)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <AIIcon size={24} sparklesPaint='white' />
              <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-white'>
                {t('risk_recognition.action')}
              </Typography>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskRecognitionFloatingCard;
